import React from 'react';
import { useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Flex, Image, Link, Text
} from 'components/ui';
import UIUtils from 'utils/UI';
import Empty from 'constants/empty';
import { radii, shadows } from 'style/config';
import { useNavigateTo } from 'components/CreatorStoreFront/helpers/csfNavigation';
import isFunction from 'utils/functions/isFunction';

const { SKELETON_ANIMATION } = UIUtils;

const DEFAULT_IMG_SRC = '/img/ufe/image-error.svg';

const handleImageError = e => (e.target.src = DEFAULT_IMG_SRC);

const titleClampStyle = {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitLineClamp: 1,
    whiteSpace: 'nowrap'
};

const renderSkeleton = () => (
    <Flex
        position='relative'
        flexDirection='column'
        alignItems='center'
        backgroundColor='white'
        borderRadius={1}
        boxShadow={shadows.light}
        overflow='hidden'
        flexShrink={0}
        width={[168, null, 240]}
        height={[220, null, 280]}
    >
        <Flex
            justifyContent='center'
            alignItems='center'
            paddingY={2}
            paddingX='9px'
            width={[168, null, 240]}
            height={[168, null, 240]}
        >
            <Flex
                flexDirection='column'
                gap={1}
            >
                {[0, 1].map(row => (
                    <Flex
                        key={row}
                        flexDirection='row'
                        gap={1}
                    >
                        {[0, 1].map(col => (
                            <Box
                                key={`${row}-${col}`}
                                width={[78, null, 107]}
                                height={[78, null, 107]}
                                borderRadius={1}
                                backgroundColor='lightGray'
                                css={SKELETON_ANIMATION}
                            />
                        ))}
                    </Flex>
                ))}
            </Flex>
        </Flex>

        <Box
            width='80%'
            height='18px'
            backgroundColor='midGray'
            borderRadius={2}
            css={SKELETON_ANIMATION}
            marginTop={2}
        />
    </Flex>
);

const renderThumbnails = (thumbnails, size) => (
    <Flex
        flexDirection='column'
        gap={1}
    >
        {[0, 1].map(row => (
            <Flex
                key={row}
                flexDirection='row'
                gap={1}
            >
                {thumbnails.slice(row * 2, row * 2 + 2).map((thumb, i) => (
                    <Image
                        key={thumb.thumbnailUrl || i}
                        src={thumb.thumbnailUrl}
                        alt={`Thumbnail ${row * 2 + i + 1}`}
                        size={size}
                        borderRadius={1}
                        onError={handleImageError}
                    />
                ))}
            </Flex>
        ))}
    </Flex>
);

const CollectionTile = ({
    item, isLoading, handle, isGridView, nextPageDataCB
}) => {
    const dispatch = useDispatch();
    const { navigateTo } = useNavigateTo(dispatch);

    const {
        collectionId,
        tileProductThumbnails = Empty.Array,
        taggedProductCount = 0,
        title = Empty.String,
        customCoverImageUrl
    } = item || Empty.Object;

    const thumbnails = tileProductThumbnails.slice(0, 4);

    const handleCollectionClick = async event => {
        event.preventDefault();
        const path = `/creators/${handle}/collections/${collectionId}`;

        isFunction(nextPageDataCB) && nextPageDataCB();

        await navigateTo(path, false, true);
    };

    if (isLoading) {
        return renderSkeleton();
    }

    const boxSize = isGridView ? [78, null, 107] : 107;
    const containerSize = isGridView ? [168, null, 240] : 240;
    const paddingSize = isGridView ? [1, null, 2] : 2;

    return (
        <Link
            underline={false}
            display='flex'
            flexDirection='column'
            overflow='hidden'
            width='100%'
            height='100%'
            css={{ position: 'relative', borderRadius: radii[2], boxShadow: shadows.light }}
            onClick={handleCollectionClick}
        >
            {taggedProductCount > 0 && (
                <Flex
                    alignItems='center'
                    gap={1}
                    position='absolute'
                    top={isGridView ? [138, null, 210] : 210}
                    left='6px'
                    paddingY={1}
                    paddingX={2}
                    borderRadius={1}
                    backgroundColor='rgba(255, 255, 255, 0.80)'
                    zIndex={1}
                >
                    <Image
                        src='/img/ufe/csf/product-count-icon.svg'
                        alt='Product Count Icon'
                        size={16}
                        disableLazyLoad
                        isPageRenderImg
                    />
                    <Text
                        fontSize='xs'
                        color='black'
                    >
                        {taggedProductCount}
                    </Text>
                </Flex>
            )}

            {customCoverImageUrl ? (
                <Box
                    is='figure'
                    width={containerSize}
                    height={containerSize}
                    backgroundColor={'white'}
                >
                    {/* Custom Cover Image */}
                    <Image
                        src={customCoverImageUrl}
                        alt={title}
                        size={containerSize}
                        css={{ objectFit: 'cover' }}
                    />
                </Box>
            ) : (
                <Flex
                    justifyContent='center'
                    alignItems='center'
                    flexWrap='wrap'
                    padding={paddingSize}
                    gap={paddingSize}
                    width={containerSize}
                    height={containerSize}
                >
                    {renderThumbnails(thumbnails, boxSize)}
                </Flex>
            )}

            <Flex
                alignItems='center'
                width='100%'
                height='100%'
                padding={2}
            >
                <Text
                    is='h3'
                    lineHeight='18px'
                    css={titleClampStyle}
                >
                    {title}
                </Text>
            </Flex>
        </Link>
    );
};

export default wrapFunctionalComponent(CollectionTile, 'CollectionTile');
